import { forwardRef, type CSSProperties, type ForwardedRef, type ReactElement } from "react";
import { Table as ZhTable, type TableProps, type TableRef } from "zhihao-ui-react";

import { AdminTableEmpty } from "@/views/components/TableEmpty";
import { useViewportTableMaxHeight } from "./useViewportTableMaxHeight";

export interface AdminDetailTableProps<
    RecordType extends object = Record<string, unknown>
> extends TableProps<RecordType> {
    /** 旧 Vue ProTable 空表的主体最小高度，避免空数据时表格和分页塌缩。 */
    emptyBodyMinHeight?: number;
}

/**
 * 详情页表格的统一适配层。
 * 详情页所有 Tab 共用旧 Vue 的“表格主体可滚动、空表保留高度、按需横向滚动”布局，业务页只传数据和列定义。
 */
const AdminDetailTableImpl = forwardRef(function AdminDetailTable<RecordType extends object>(
    {
        className,
        data,
        append,
        empty,
        emptyBodyMinHeight = 400,
        emptyText,
        height,
        maxHeight,
        scrollbarAlwaysOn,
        style,
        ...props
    }: AdminDetailTableProps<RecordType>,
    ref: ForwardedRef<TableRef<RecordType>>
) {
    // 旧 Vue 仅在表格确实为空时通过空态容器保留最小高度，避免非空表格被无条件撑高。
    const isEmpty = Array.isArray(data) && data.length === 0;
    const classNames = ["admin-detail-table", className, isEmpty ? "admin-detail-table--empty-400" : ""]
        .filter(Boolean)
        .join(" ");
    const wrapperStyle = {
        ...style,
        ...(isEmpty ? { "--admin-detail-table-empty-min-height": `${emptyBodyMinHeight}px` } : {})
    } as CSSProperties;
    // 迁移列表与旧 Vue ProTable 一样根据当前位置约束长表；详情及显式高度调用保持原配置。
    const { hostRef, maxHeight: viewportMaxHeight } = useViewportTableMaxHeight(
        Boolean(append) && height == null && maxHeight == null,
        data?.length
    );

    return (
        <div ref={hostRef} className={classNames} data-admin-detail-table="true" style={wrapperStyle}>
            <ZhTable
                {...props}
                append={append}
                data={data}
                height={height}
                maxHeight={maxHeight ?? viewportMaxHeight}
                ref={ref}
                className={className}
                empty={empty ?? <AdminTableEmpty text={emptyText} />}
                emptyText={emptyText}
                // Vue Element Table 不常驻横向滚动条；只有实际内容溢出时才允许出现。
                scrollbarAlwaysOn={scrollbarAlwaysOn ?? false}
            />
        </div>
    );
});

// forwardRef 无法从调用处自动保留泛型，这里补回表格行类型推导，避免业务列定义退化为 unknown。
const AdminDetailTable = AdminDetailTableImpl as <RecordType extends object = Record<string, unknown>>(
    props: AdminDetailTableProps<RecordType> & { ref?: ForwardedRef<TableRef<RecordType>> }
) => ReactElement;

export { AdminDetailTable };
export default AdminDetailTable;
